import LeaderboardStats from '../LeaderboardStats';
import React, { useState, useEffect } from 'react';
import api from '../api';
import '../styles/Leaderboard.css';
import { LeaderboardFrame, LeaderboardPanel, DateSelector, formatLeaderboardDate } from '@precisionpros/game-ui';

const addDays = (dateStr, days) => {
  const date = new Date(`${dateStr}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().split('T')[0];
};

function Leaderboard({ onBack, onHowToPlay, playerName = '', headerLinks = [], homeHref, centralStatusReady = false, centralStatusPlayed = false }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [gameMode, setGameMode] = useState('standard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [todayDate, setTodayDate] = useState(null);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [boardError, setBoardError] = useState('');
  const [boardLoading, setBoardLoading] = useState(false);
  const [hasPlayedToday, setHasPlayedToday] = useState(null);
  const checkingPlayed = !centralStatusReady;

  useEffect(() => {
    if (!centralStatusReady) return;
    const fetchLeaderboard = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await api.leaderboard(selectedDate);
        setLeaderboard(data.results);
        setGameMode(data.game_mode || 'standard');
        setSelectedDate(data.date);
        if (!todayDate) {
          setTodayDate(data.date);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, [selectedDate, todayDate, centralStatusReady]);

  useEffect(() => {
    if (centralStatusReady) setHasPlayedToday(centralStatusPlayed);
  }, [centralStatusReady, centralStatusPlayed]);

  const formatTime = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };


  const getBoardBounds = (finalGrid) => {
    if (!Array.isArray(finalGrid) || finalGrid.length === 0) {
      return { minRow: 0, maxRow: 0, minCol: 0, maxCol: 0 };
    }

    return finalGrid.reduce((bounds, cell) => ({
      minRow: Math.min(bounds.minRow, cell.row),
      maxRow: Math.max(bounds.maxRow, cell.row),
      minCol: Math.min(bounds.minCol, cell.col),
      maxCol: Math.max(bounds.maxCol, cell.col)
    }), {
      minRow: Infinity,
      maxRow: -Infinity,
      minCol: Infinity,
      maxCol: -Infinity
    });
  };

  const handleViewBoard = async (entry) => {
    setBoardError('');
    if (isViewingToday && hasPlayedToday === false) {
      setBoardError('Play today before viewing completed boards.');
      return;
    }

    setBoardLoading(true);
    try {
      const requesterName = playerName || localStorage.getItem('playerName') || localStorage.getItem('buildpro_player_name') || '';
      const data = await api.playerBoard(entry.player_name, entry.date_played, requesterName);
      setSelectedBoard(data);
    } catch (err) {
      setBoardError(err.message);
    } finally {
      setBoardLoading(false);
    }
  };

  const changeDate = (days) => {
    setSelectedBoard(null);
    setBoardError('');
    setSelectedDate((current) => addDays(current || todayDate, days));
  };

  const isViewingToday = selectedDate && todayDate && selectedDate === todayDate;
  const shouldHideToday = isViewingToday && hasPlayedToday === false;

  if (loading) return (
    <LeaderboardFrame
      game={{ name: 'Build', wordmarkMain: 'Build', wordmarkAccent: 'Pro', badge: 'B', accent: '#0f766e' }}
      title="Leaderboard"
      links={headerLinks}
      homeHref={homeHref}
      homeLabel="← Games Hub"
      showPrePlayBanner={false}
      dateSelector={<DateSelector dateLabel={formatLeaderboardDate(selectedDate)} onPrevious={() => {}} onNext={() => {}} />}
    >
      <LeaderboardPanel game={{ name: 'Build', wordmarkMain: 'Build', wordmarkAccent: 'Pro', badge: 'B', accent: '#0f766e' }} subtitle="Loading leaderboard…">
        <p className="no-data">Loading...</p>
      </LeaderboardPanel>
    </LeaderboardFrame>
  );

  return (
    <LeaderboardFrame game={{ name: 'Build', wordmarkMain: 'Build', wordmarkAccent: 'Pro', badge: 'B', accent: '#0f766e' }} title="Leaderboard" links={headerLinks} homeHref={homeHref} homeLabel="← Games Hub" showPrePlayBanner={shouldHideToday} playHref={`${window.location.origin}/`} howToPlayHref={`${window.location.origin}/how-to-play`} dateSelector={<DateSelector dateLabel={formatLeaderboardDate(selectedDate)} onPrevious={() => changeDate(-1)} onNext={() => changeDate(1)} />}>
      <h2>Today’s Results</h2>
      {!shouldHideToday && <LeaderboardPanel game={{ name: 'Build', wordmarkMain: 'Build', wordmarkAccent: 'Pro', badge: 'B', accent: '#0f766e' }} subtitle={`Completed boards, ranked by ${gameMode === 'points' ? 'points' : 'time'}.`}>

        {error && <div className="error-message">{error}</div>}

        {leaderboard.length === 0 ? (
          <p className="no-data">No scores for this day.</p>
        ) : (
          <table className="leaderboard-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Player</th>
                <th>{gameMode === 'points' ? 'Points' : 'Time'}</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((entry, idx) => (
                <tr key={idx} className="leaderboard-row">
                  <td className="rank">#{entry.rank || idx + 1}{entry.tied ? ' (tie)' : ''}</td>
                  <td className="player-name">{entry.player_name}</td>
                  <td className="time">{gameMode === 'points' ? `${entry.points || 0}` : formatTime(entry.time_taken)}</td>
                  <td className="action">
                    <button
                      className="view-btn"
                      onClick={() => handleViewBoard(entry)}
                      disabled={boardLoading || checkingPlayed || shouldHideToday}
                      title={shouldHideToday ? 'Play today before viewing completed boards' : 'View completed board'}
                    >
                      View Board
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

      </LeaderboardPanel>}

      <LeaderboardStats endpoint={api.leaderboardRecordsUrl} date={selectedDate} pointsFriday />

      {boardError && <div className="error-message leaderboard-error">{boardError}</div>}

      {selectedBoard && (
        <div className="board-modal-overlay" onClick={() => setSelectedBoard(null)}>
          <div className="board-modal" onClick={(e) => e.stopPropagation()}>
            <div className="board-modal-header">
              <h3>{selectedBoard.player_name}'s Board</h3>
              <button className="board-modal-close" onClick={() => setSelectedBoard(null)} aria-label="Close board">
                ×
              </button>
            </div>

            <div className="board-modal-meta">
              <span>Date: {selectedBoard.date}</span>
              <span>Time: {formatTime(selectedBoard.time_taken)}</span>
            </div>

            <div className="board-preview" role="img" aria-label={`${selectedBoard.player_name}'s completed board`}>
              {(() => {
                const bounds = getBoardBounds(selectedBoard.final_grid);
                const cells = new Map((selectedBoard.final_grid || []).map((cell) => [`${cell.row},${cell.col}`, cell.letter]));
                const rows = [];

                for (let row = bounds.minRow; row <= bounds.maxRow; row += 1) {
                  const cols = [];
                  for (let col = bounds.minCol; col <= bounds.maxCol; col += 1) {
                    const letter = cells.get(`${row},${col}`) || '';
                    cols.push(
                      <div key={`${row},${col}`} className={`board-preview-cell ${letter ? 'filled' : ''}`}>
                        {letter}
                      </div>
                    );
                  }
                  rows.push(
                    <div key={row} className="board-preview-row">
                      {cols}
                    </div>
                  );
                }

                return rows;
              })()}
            </div>
          </div>
        </div>
      )}
    </LeaderboardFrame>
  );
}

export default Leaderboard;
